import { useCallback, useEffect, useRef, useState } from 'react';

import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { createEvaluation, updateEvaluation } from 'api/endpoints/children';
import Button from 'components/Button';
import LoadingComponent from 'components/LoadingComponent';
import { ENUM_TYPE_REPORT_STATUS, ENUM_TYPE_VIDEO } from 'constants/enum';
import useChildren from 'hooks/useChildren';
import useReport from 'hooks/useReport';
import { useResize } from 'hooks/useResize';
import { toastError, toastSuccess } from 'lib/toast';
import Header from 'pages/Users/Detail/ReportRegistration/_components/Header';
import AppRoutes from 'routes/routes';
import { setCreateReportData } from 'store/actions/children';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { calculateWidth, convertToQueryString, handleError } from 'utils/helper';

import PopupAddExerciseVideo from '../_components/PopupAddExerciseVideo/PopupAddExerciseVideo';
import RecentAnswerResultsAndVideoResults from '../_components/RecentAnswerResultsAndVideoResults/RecentAnswerResultsAndVideoResults';
import RecentEvaluationAnalysisResults from '../_components/RecentEvaluationAnalysisResults/RecentEvaluationAnalysisResults';
import RecentExerciseVideoSelection from '../_components/RecentExerciseVideoSelection/RecentExerciseVideoSelection';

const ExclamationCircle = require('assets/resources/ExclamationCircle.svg').default;
const ExclamationCircle2 = require('assets/resources/ExclamationCircle2.svg').default;

const INIT_VALUES = {
  comment: '',
  scores: [],
  categories: [],
  list_dates: {
    reports: [],
    analysis: [],
  },
  status: ENUM_TYPE_REPORT_STATUS.PROCESSING,
};

const ReportRegistration = () => {
  const navigator = useNavigate();

  const dispatch = useDispatch();

  const { me } = useSelector((state) => state.auth);
  const { childrenDetail, createReportData } = useSelector((state) => state.children);

  const { getEvaluationDetail, getDetailChild } = useChildren();
  const { width } = useResize();
  const { getReportByDate } = useReport();

  const [isLoading, setIsLoading] = useState(true);
  const [showPopupAddExerciseVideo, setShowPopupAddExerciseVideo] = useState(false);
  const [datesHasVideo, setDatesHasVideo] = useState([]);
  const [datesHasReport, setDatesHasReport] = useState([]);
  const [selectedVideoASE, setSelectedVideoASE] = useState([]);
  const [selectedVideoDAY, setSelectedVideoDAY] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(ENUM_TYPE_REPORT_STATUS.PROCESSING);
  const [typeVideo, setTypeVideo] = useState(ENUM_TYPE_VIDEO.ASE);
  const [idEdit, setIdEdit] = useState(-1);

  const isCallingApi = useRef(false);

  const form = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: INIT_VALUES,
    onSubmit: async (values) => {
      const handleSubmit = async () => {
        const data = {
          ...values,
          categories: [...selectedVideoASE, ...selectedVideoDAY],
        };

        // UPDATE EVALUATION
        try {
          if (idEdit !== -1) {
            dispatch(setLoading({ isOpen: true }));
            try {
              const response = await updateEvaluation(idEdit, data);
              toastSuccess(response?.message);
            } catch (error) {
              toastError(error?.message);
            } finally {
              dispatch(setLoading({ isOpen: false }));
            }
          } else {
            // CREATE EVALUATION
            dispatch(setLoading({ isOpen: true }));
            try {
              const response = await createEvaluation({
                ...data,
                child_id: childrenDetail?.id,
              });

              setIdEdit(response.data.id);

              if (values.status === ENUM_TYPE_REPORT_STATUS.PROCESSING) {
                toastSuccess('一時保存されました。');
              } else {
                toastSuccess('入力完了しました。');
              }
            } catch (error) {
              toastError(error?.message);
            } finally {
              dispatch(setLoading({ isOpen: false }));
            }
          }
        } catch (error) {
          handleError(error);
        } finally {
          await getDetailChild(childrenDetail?.id);
        }

        if (values.status === ENUM_TYPE_REPORT_STATUS.FINISH) {
          setIsLoading(true);
          await form.resetForm(INIT_VALUES);
          setDatesHasVideo([]);
          setDatesHasReport([]);
          setSelectedVideoASE([]);
          setSelectedVideoDAY([]);
          setIdEdit(-1);
          setCurrentStatus(ENUM_TYPE_REPORT_STATUS.PROCESSING);
          await handleNewCreateEvaluation();
          dispatch(setCreateReportData({ reportId: null }));
          navigator(AppRoutes.evaluationAnalysis);
        } else {
          setCurrentStatus(values.status);
        }
        dispatch(clearModal());
      };

      const confirmLock = () => {
        dispatch(
          setModal({
            isOpen: true,
            type: 'confirm',
            title: '評価結果の入力を完了します。以降、修正や入力はできなくなります。よろしいですか？',
            confirmText: '入力完了',
            cancelText: 'キャンセル ',
            icon: ExclamationCircle,
            onConfirm: handleSubmit,
            onCancel: () => {
              dispatch(clearModal());
              form.setFieldValue('status', currentStatus);
            },
          })
        );
      };

      if (
        currentStatus === ENUM_TYPE_REPORT_STATUS.PROCESSING &&
        [ENUM_TYPE_REPORT_STATUS.LOCK, ENUM_TYPE_REPORT_STATUS.FINISH].includes(values.status) &&
        values.comment.trim() === ''
      ) {
        dispatch(
          setModal({
            isOpen: true,
            type: 'confirm',
            title: 'コメント欄が未入力ですが、このまま入力完了してもよろしいですか。',
            confirmText: '入力に戻る',
            cancelText: '入力完了',
            icon: ExclamationCircle2,
            onConfirm: values.status === ENUM_TYPE_REPORT_STATUS.LOCK ? confirmLock : handleSubmit,
            onCancel: () => {
              dispatch(clearModal());
              form.setFieldValue('status', currentStatus);
            },
          })
        );
      } else {
        if (values.status === ENUM_TYPE_REPORT_STATUS.LOCK) {
          confirmLock();
        } else {
          handleSubmit();
        }
      }
    },
  });

  const handleNewCreateEvaluation = async () => {
    // GET DATES HAS VIDEO
    if (createReportData?.reportId) {
      setDatesHasVideo([createReportData?.reportId]);
      form.setFieldValue('list_dates.reports', [createReportData?.reportId]);
      form.setFieldValue('report_id', createReportData?.reportId);
    }

    // GET DATES HAS REPORT
    const queryStringGetDatesHasReport = convertToQueryString({ page: 1, page_size: 3 });
    const responseDatesHasReport = await getReportByDate(queryStringGetDatesHasReport);
    if (responseDatesHasReport) {
      const datesString = responseDatesHasReport.map((item) => item.id);
      setDatesHasReport(datesString);
      form.setFieldValue('list_dates.analysis', datesString);
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleEditEvaluation = async (session_id) => {
    try {
      const detail = await getEvaluationDetail(session_id);
      if (detail) {
        setIdEdit(detail.id);
        setCurrentStatus(detail.status);
        setDatesHasVideo(detail.list_dates.reports);
        setDatesHasReport(detail.list_dates.analysis);

        const categoriesASE = detail.categories
          .filter((item) => item.videocategory_type === ENUM_TYPE_VIDEO.ASE)
          .map((item) => item.videocategory_id);
        const categoriesDAY = detail.categories
          .filter((item) => item.videocategory_type === ENUM_TYPE_VIDEO.DAY)
          .map((item) => item.videocategory_id);

        setSelectedVideoASE(categoriesASE);
        setSelectedVideoDAY(categoriesDAY);

        form.setValues({
          comment: detail.comment,
          scores: detail.scores,
          categories: [],
          list_dates: {
            reports: detail.list_dates.reports,
            analysis: detail.list_dates.analysis,
          },
          status: detail.status,
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  const checkIsEdit = useCallback(async () => {
    if (me && childrenDetail?.id && !isCallingApi.current) {
      isCallingApi.current = true;
      if (createReportData?.evaluationId) {
        await handleEditEvaluation(createReportData?.evaluationId);
        return;
      }
      await handleNewCreateEvaluation();
    }
  }, [me, childrenDetail?.id, createReportData?.evaluationId]);

  useEffect(() => {
    checkIsEdit();
  }, [checkIsEdit]);

  // EVENTS
  const handleSelectVideo = (selectedVideo) => {
    if (typeVideo === ENUM_TYPE_VIDEO.ASE) {
      setSelectedVideoASE(selectedVideo);
    } else {
      setSelectedVideoDAY(selectedVideo);
    }
  };

  const handleRemoveVideo = (id, type) => {
    if (type === ENUM_TYPE_VIDEO.ASE) {
      const newSelectedVideoASE = selectedVideoASE.filter((item) => item !== id);
      setSelectedVideoASE(newSelectedVideoASE);
    } else if (type === ENUM_TYPE_VIDEO.DAY) {
      setSelectedVideoDAY((prev) => prev.filter((item) => item !== id));
    }
  };

  if (!createReportData?.reportId) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Link to="/evaluation-analysis" className="text-blue-600 underline">
          <Button>評価分析一覧</Button>
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <LoadingComponent />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <Header form={form} />
        <div className="flex gap-4 h-[calc(100%-68px)]">
          <div style={calculateWidth(400, 1920, width)}>
            <RecentAnswerResultsAndVideoResults datesHasVideo={datesHasVideo} datesReportRegistration={datesHasReport} isReportRegistration={true} />
          </div>
          <div style={calculateWidth(600, 1920, width)}>
            <RecentEvaluationAnalysisResults isReportRegistration={true} form={form} currentStatus={currentStatus} />
          </div>
          <div className="!w-full !max-w-full" style={calculateWidth(720, 1920, width)}>
            <RecentExerciseVideoSelection
              isReportRegistration={true}
              selectedVideoASE={selectedVideoASE}
              selectedVideoDAY={selectedVideoDAY}
              setTypeVideo={setTypeVideo}
              handleRemoveVideo={handleRemoveVideo}
              setShowPopupAddExerciseVideo={setShowPopupAddExerciseVideo}
              currentStatus={currentStatus}
            />
          </div>
        </div>
      </div>
      {showPopupAddExerciseVideo && (
        <PopupAddExerciseVideo
          typeVideo={typeVideo}
          originalVideoSelection={typeVideo === ENUM_TYPE_VIDEO.ASE ? selectedVideoASE : selectedVideoDAY}
          closePopup={() => setShowPopupAddExerciseVideo(false)}
          onSelectVideo={handleSelectVideo}
        />
      )}
    </>
  );
};

export default ReportRegistration;
